import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Sparkles, TrendingUp, PackageCheck, Clock, DollarSign, Wallet, AlertTriangle, TrendingDown } from "lucide-react";
import HelpLink from "@/components/HelpLink";

/**
 * Rx Series Prescribing-Efficiency widget for the Admin Overview tab.
 *
 * Important framing — prescribers are paid ONCE per series (one rx-write
 * ceremony covers all 3 monthly orders or all 2 royal orders). So the
 * meaningful productivity metric for a prescriber is *shipments per pay
 * event*, not raw shipment count.  We surface both: dollar pipeline (for
 * finance) and shipments-per-pay-event (for prescriber morale).
 */
export default function RxSeriesEfficiencyCard() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      API.get(`/admin/rx-series/stats?days=${windowDays}`).then((r) => r.data).catch(() => null),
      API.get(`/admin/rx-series/approval-mix-trend?days=${windowDays}`).then((r) => r.data).catch(() => null),
    ])
      .then(([stats, t]) => {
        if (!alive) return;
        setData(stats);
        setTrend(t);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [windowDays]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="rx-series-card-loading">
        <p className="text-sm text-slate-500">Loading rx-series stats…</p>
      </div>
    );
  }

  const sys = data?.system || { series_generated: 0, shipments_queued: 0, shipments_released: 0, queued_value_usd: 0, series_pre_screened: 0, series_standard: 0 };
  const top = (data?.per_prescriber || []).slice(0, 5);
  const psTotal = (sys.series_pre_screened || 0) + (sys.series_standard || 0);
  const psPct = psTotal > 0 ? Math.round((sys.series_pre_screened / psTotal) * 100) : 0;
  const stdPct = 100 - psPct;

  return (
    <div
      className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5"
      data-testid="rx-series-efficiency-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm inline-flex items-center gap-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Rx Series — Prescribing Efficiency
              <HelpLink sectionId="rx-series-efficiency-widget" title="Training: Rx Series efficiency widget" size={13} />
            </h3>
            <p className="text-[11px] text-slate-500">One pay event per series · multi-shipment leverage</p>
          </div>
        </div>
        <select
          value={windowDays}
          onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
          className="text-xs border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 bg-white dark:bg-slate-800"
          data-testid="rx-series-window-select"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* System totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Stat icon={TrendingUp} label="Series generated" value={sys.series_generated} color="purple" />
        <Stat icon={Clock} label="Shipments queued" value={sys.shipments_queued} color="amber" />
        <Stat icon={PackageCheck} label="Shipments released" value={sys.shipments_released} color="emerald" />
        <Stat icon={DollarSign} label="Queued pipeline" value={`$${(sys.queued_value_usd || 0).toLocaleString()}`} color="slate" />
        <Stat icon={Wallet} label="Projected comp pool" value={`$${(sys.projected_comp_usd || 0).toLocaleString()}`} color="indigo" />
      </div>

      {/* Pre-Screened vs Standard Review approval mix — at-a-glance read on
          whether the pre-screened bonus is moving prescriber behavior and
          whether the screening logic is too strict / too loose. */}
      {psTotal > 0 ? (
        <div className="mb-5 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-3" data-testid="approval-mix-bar">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] uppercase tracking-wider text-slate-600 dark:text-slate-300 font-semibold inline-flex items-center gap-1">
              Approval mix · last {data?.window_days || 30}d
              <HelpLink sectionId="rx-series-efficiency-widget" title="Training: how to read the approval mix" size={11} />
            </p>
            <p className="text-[11px] text-slate-500 tabular-nums" data-testid="approval-mix-counts">
              <span className="text-purple-700 dark:text-purple-300 font-semibold">{sys.series_pre_screened || 0}</span> pre-screened
              <span className="text-slate-400 px-1.5">·</span>
              <span className="text-blue-700 dark:text-blue-300 font-semibold">{sys.series_standard || 0}</span> standard review
            </p>
          </div>
          <div className="flex h-6 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            {psPct > 0 && (
              <div
                className="flex items-center justify-center bg-purple-500 text-white text-[10px] font-semibold tabular-nums transition-all"
                style={{ width: `${psPct}%` }}
                title={`${sys.series_pre_screened} of ${psTotal} approvals were pre-screened (${psPct}%)`}
                data-testid="approval-mix-ps-bar"
              >
                {psPct >= 8 ? `${psPct}%` : ""}
              </div>
            )}
            {stdPct > 0 && (
              <div
                className="flex items-center justify-center bg-blue-500 text-white text-[10px] font-semibold tabular-nums transition-all"
                style={{ width: `${stdPct}%` }}
                title={`${sys.series_standard} of ${psTotal} approvals needed standard review (${stdPct}%)`}
                data-testid="approval-mix-std-bar"
              >
                {stdPct >= 8 ? `${stdPct}%` : ""}
              </div>
            )}
          </div>
          <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1.5 italic">
            Higher pre-screened % = the screening logic is matching more routine cases. Clinician still reviews every order regardless.
          </p>

          {/* Daily PS adoption trend — 30-day sparkline.  Each column is one
              day, height = total approvals, color = pre-screened share.  Lets
              you spot rule-tweak inflection points and onboarding dips. */}
          <ApprovalMixSparkline trend={trend} />

          {/* Calibration drift alert — surfaces only on meaningful
              week-over-week drift (>=15pts) to avoid noise from
              day-to-day variance. */}
          <CalibrationDriftAlert trend={trend} />
        </div>
      ) : null}

      {/* Per-prescriber leaderboard */}
      {top.length === 0 ? (
        <p className="text-xs text-slate-400 italic">No series generated in this window yet.</p>
      ) : (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">
            Top prescribers · ranked by projected commission
            {data?.commission_rates && (
              <span className="text-slate-400 normal-case tracking-normal font-normal ml-2">
                (Monthly ${data.commission_rates.monthly_usd?.toFixed(2)} · Royal ${data.commission_rates.royal_usd?.toFixed(2)} per series)
              </span>
            )}
          </p>
          <ul className="space-y-1.5" data-testid="rx-series-leaderboard">
            {top.map((p) => {
              const psCount = p.series_pre_screened || 0;
              const stdCount = p.series_standard || 0;
              const mixTotal = psCount + stdCount;
              const psPctRow = mixTotal > 0 ? Math.round((psCount / mixTotal) * 100) : 0;
              return (
                <li
                  key={p.prescriber_id}
                  className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/40 rounded-lg px-3 py-2 text-sm gap-3"
                  data-testid={`rx-series-row-${p.prescriber_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-white truncate">{p.prescriber_name}</p>
                    <p className="text-[11px] text-slate-500">
                      {p.series_generated} series · {p.shipments_queued} queued · {p.shipments_per_pay_event}× ship/pay
                    </p>
                  </div>
                  {/* Per-prescriber PS-vs-Standard mini-bar — surfaces individual
                      screening workflow (over-cautious vs under-reviewing). */}
                  <div className="hidden sm:flex flex-col items-end shrink-0 w-32" data-testid={`rx-series-mix-${p.prescriber_id}`}>
                    <div className="flex items-center gap-1 text-[10px] tabular-nums text-slate-600 dark:text-slate-300">
                      <span className="text-purple-700 dark:text-purple-300 font-semibold" title="Pre-Screened approvals">{psCount} PS</span>
                      <span className="text-slate-400">/</span>
                      <span className="text-blue-700 dark:text-blue-300 font-semibold" title="Standard-Review approvals">{stdCount} Std</span>
                      <span
                        className={`ml-1 font-mono font-semibold ${psPctRow >= 50 ? "text-purple-700 dark:text-purple-300" : "text-blue-700 dark:text-blue-300"}`}
                        title={`${psPctRow}% of this prescriber's series were pre-screened`}
                        data-testid={`rx-series-mix-pct-${p.prescriber_id}`}
                      >
                        {psPctRow}%
                      </span>
                    </div>
                    {mixTotal > 0 && (
                      <div className="flex h-1.5 w-full mt-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                        {psPctRow > 0 && <div className="bg-purple-500" style={{ width: `${psPctRow}%` }} />}
                        {psPctRow < 100 && <div className="bg-blue-500" style={{ width: `${100 - psPctRow}%` }} />}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0 pl-1">
                    <p className="text-base font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums" data-testid={`rx-series-comp-${p.prescriber_id}`}>
                      ${(p.projected_comp_usd || 0).toLocaleString()}
                    </p>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider">projected comp</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-3 italic">
        Projected comp = flat rate per series (admin-tunable in Loyalty & Coupons → Commission Rates). Pay-once-per-series — independent of queued shipment count.
      </p>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  const palette = {
    purple: "bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300",
    amber: "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300",
    emerald: "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300",
    indigo: "bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300",
    slate: "bg-slate-50 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300",
  }[color] || "bg-slate-50 text-slate-700";
  return (
    <div className={`rounded-xl px-3 py-2.5 ${palette}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] uppercase tracking-wider font-semibold">{label}</p>
      </div>
      <p className="text-xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

/**
 * Daily PS-vs-Std sparkline.  Each bar = one calendar day in the window.
 * Bar HEIGHT = total approvals that day (max-normalized so the busiest day
 * fills the chart).  WITHIN each bar, the purple slice = pre-screened share
 * and the blue slice = standard-review share, so you read both volume and
 * mix at once.  Days with 0 approvals render as a thin gray baseline so the
 * spacing stays honest.
 */
function ApprovalMixSparkline({ trend }) {
  const rows = trend?.rows || [];
  if (rows.length === 0) return null;
  const maxTotal = Math.max(1, ...rows.map((r) => r.total || 0));
  const totals = trend?.totals || { pre_screened: 0, standard: 0, pct: 0 };
  const hasAny = (totals.pre_screened + totals.standard) > 0;

  return (
    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700" data-testid="approval-mix-sparkline">
      <div className="flex items-baseline justify-between mb-1.5">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold inline-flex items-center gap-1">
          PS adoption trend · last {rows.length}d
          <HelpLink sectionId="rx-series-efficiency-widget" title="Training: how to read the PS adoption sparkline" size={11} />
        </p>
        {hasAny && (
          <p className="text-[10px] text-slate-500 tabular-nums">
            Window avg <span className="font-semibold text-purple-700 dark:text-purple-300" data-testid="trend-window-pct">{totals.pct}%</span>
          </p>
        )}
      </div>
      {hasAny ? (
        <div className="flex items-end gap-px h-12" role="img" aria-label="Daily pre-screened vs standard-review approvals">
          {rows.map((r) => {
            const heightPct = (r.total / maxTotal) * 100;
            const psShare = r.total ? (r.pre_screened / r.total) * 100 : 0;
            const stdShare = r.total ? 100 - psShare : 0;
            const tip = r.total === 0
              ? `${r.date} · no approvals`
              : `${r.date} · ${r.pre_screened} PS / ${r.standard} Std · ${r.pct}%`;
            return (
              <div
                key={r.date}
                title={tip}
                data-testid={`trend-bar-${r.date}`}
                className="flex-1 flex flex-col justify-end relative group"
                style={{ minWidth: "2px" }}
              >
                {r.total === 0 ? (
                  <div className="h-px bg-slate-200 dark:bg-slate-700 rounded-sm" />
                ) : (
                  <div
                    className="flex flex-col rounded-sm overflow-hidden transition-opacity group-hover:opacity-80"
                    style={{ height: `${Math.max(heightPct, 6)}%` }}
                  >
                    {psShare > 0 && <div className="bg-purple-500" style={{ height: `${psShare}%` }} />}
                    {stdShare > 0 && <div className="bg-blue-500" style={{ height: `${stdShare}%` }} />}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-[11px] text-slate-400 italic py-1">No approvals in this window yet — sparkline will populate as orders are signed.</p>
      )}
      <div className="flex items-center justify-between mt-1 text-[10px] text-slate-400 dark:text-slate-500">
        <span>{rows[0]?.date?.slice(5)}</span>
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500" /> PS</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Std</span>
        </span>
        <span>{rows[rows.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
}


/**
 * Calibration drift alert.  Compares the most-recent-7-day PS% against the
 * prior-7-day PS%; surfaces an amber banner when |delta| >= 15 points.
 *
 * Why 15pts and 7-day buckets?
 *  • 7-day windows smooth out single-day spikes (especially for low-volume
 *    days like weekends).
 *  • 15pts is the conservative threshold for "actually different" without
 *    spamming during normal week-to-week variance.
 *  • Both windows must have >= 5 series otherwise the % is too noisy.
 */
function CalibrationDriftAlert({ trend }) {
  const rows = trend?.rows || [];
  if (rows.length < 14) return null;

  // Aggregate the most-recent 7 days and the 7 days before that.
  const recent = rows.slice(-7);
  const prior = rows.slice(-14, -7);
  const sum = (rs) => rs.reduce(
    (acc, r) => ({ ps: acc.ps + (r.pre_screened || 0), std: acc.std + (r.standard || 0) }),
    { ps: 0, std: 0 },
  );
  const r = sum(recent);
  const p = sum(prior);
  const rTotal = r.ps + r.std;
  const pTotal = p.ps + p.std;
  // Both windows need enough volume to draw a meaningful conclusion.
  if (rTotal < 5 || pTotal < 5) return null;
  const rPct = Math.round((r.ps / rTotal) * 100);
  const pPct = Math.round((p.ps / pTotal) * 100);
  const delta = rPct - pPct;
  if (Math.abs(delta) < 15) return null;

  const dropped = delta < 0;
  const Icon = dropped ? TrendingDown : AlertTriangle;
  const palette = dropped
    ? "bg-amber-50 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700/50 text-amber-900 dark:text-amber-200"
    : "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700/50 text-emerald-900 dark:text-emerald-200";
  const summary = dropped
    ? `PS% dropped ${Math.abs(delta)}pts this week (${pPct}% → ${rPct}%)`
    : `PS% rose ${delta}pts this week (${pPct}% → ${rPct}%)`;
  const followUp = dropped
    ? "Possible causes: new prescriber onboarding (cohort being cautious), screening-rule tightening, or a patient-mix shift toward complex histories. Review the per-prescriber breakdown to localize."
    : "Possible causes: screening-rule loosening, a rule-tweak landed, or a patient-mix shift toward simpler histories. Confirm clinician review cadence hasn't dropped.";

  return (
    <div
      className={`mt-3 rounded-lg border px-3 py-2.5 text-xs flex items-start gap-2.5 ${palette}`}
      data-testid="ps-drift-alert"
      role="alert"
    >
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold" data-testid="ps-drift-summary">{summary}</p>
        <p className="text-[11px] opacity-80 mt-0.5">
          Recent 7d: <span className="tabular-nums font-mono">{r.ps}/{rTotal}</span> · Prior 7d: <span className="tabular-nums font-mono">{p.ps}/{pTotal}</span>
        </p>
        <p className="text-[11px] opacity-75 mt-1 italic">{followUp}</p>
      </div>
    </div>
  );
}
